import { AuthorizationService } from "../authorization/authorization";
import { LoggingService, UserVisibleMessage } from "../loggingSvc";
import { LocalizationService } from "../../../ShellCommon/localization/localization";
import { SelectMenuItem } from "../../../ShellCommon/Controls/selectmenu/selectmenu";
import { MenuItem } from "../../../ShellCommon/Controls/poppable-menu/menu";
import { PresenceState } from "./presence";
import { NavbarRegistrationServiceImplementation } from "./navbar-registration-service";
import { FlightingServiceImplementation } from "../flightingSvc";
import { ApplicationContextService } from "../context/application-context-service";
import { AvailabilityServiceImpl } from "../availability-status/availability-service";
import { LogoutService } from "../authentication/logout-service";
import { GraphApiService } from "../graph-api/graph-api-service";
import {
  ModalityConstants,
  AvailabilityEvent,
  CantileverStateConstants
} from "../availability-status/availability-constants";
import * as AvailabilityModels from "../availability-status/availability-models";
import { SdPresenceStatesCollection, PresenceStateImplementation } from "./presence";
import { parentStateName } from "../../preference/preferenceController";
import { ServiceDeskConfig } from "../../Startup/serviceDeskConfig";
import * as ChannelsModel from "../agent-channels/channels-models";
import { ChannelsConstants } from "../agent-channels/channels-constants";
import { MsalAuthenticationServiceImplementation } from "../msal/msal-authentication-service";

const defaultImageUrl: string = "/content/svg-icons/CMAT_icon_profile_Core-blue_white.svg";

export class NavBarController {
  public profileImgUrl: string | ArrayBuffer = defaultImageUrl;
  public agentName: string;
  public menuItems: SelectMenuItem[];
  public agentPresenceGlyph: string;
  public agentPresenceString: string;
  public menuItemsWithStatus: MenuItem[] = [];
  public availabilityServiceFlight = false;
  public shouldRetriveDoDCases: boolean = false;
  public caseTypeOptions: SelectMenuItem[];
  public caseTypeSelected: SelectMenuItem;

  private readonly locAppId: string = "ServiceDesk";
  private readonly presenceRight: string = "SDHome_Presence";
  private readonly awayGlyph: string = "sd-glyph-agent-away";
  private readonly unknownGlyph: string = "sd-glyph-agent-unknown";
  private readonly awayLocId: string = "_AGENT_DESKTOP_NAV_BAR_PRESENCE_AWAY_";
  private readonly unknownLocId: string = "_AGENT_DESKTOP_NAV_BAR_PRESENCE_UNKNOWN_";
  private previousPresence: PresenceState;
  private currentPresence: PresenceState;
  private inStatusUpdate: boolean;
  private awayPresenceState: PresenceState;
  email: any;

  // @ngInject
  constructor(
    private graphApiService: GraphApiService,
    public navbarRegistrationService: NavbarRegistrationServiceImplementation,
    private applicationContextService: ApplicationContextService,
    private $state: ng.ui.IStateService,
    private $scope: ng.IScope,
    private flightingService: FlightingServiceImplementation,
    private sdLocalizationService: LocalizationService,
    private logoutService: LogoutService,
    private $window: ng.IWindowService,
    private msalAuthenticationService: MsalAuthenticationServiceImplementation,
    private authorizationService: AuthorizationService,
    private sdkAvailabilityService: AvailabilityServiceImpl,
    private loggingService: LoggingService,
    private sdConfig: ServiceDeskConfig,
    private channelsConstants: ChannelsConstants,
    private modalityConstants: ModalityConstants,
    private cantileverStateConstants: CantileverStateConstants,
    private sdPresenceStatesCollection: SdPresenceStatesCollection
  ) {
    navbarRegistrationService.configure();
    this.availabilityServiceFlight = true;
    this.configureMeDropdownWithAvailability();
    this.shouldRetriveDoDCases = this.hasDoDAccess();
    let defaultValue = this.sdLocalizationService.getLocalizedString("ServiceDesk", "_NAV_BAR_DEFAULT_");
    let usValue = this.sdLocalizationService.getLocalizedString("ServiceDesk", "_NAV_BAR_US_");
    this.caseTypeOptions = [
      { itemName: defaultValue, itemValue: defaultValue },
      { itemName: usValue, itemValue: usValue }
    ];
    this.caseTypeSelected = this.caseTypeOptions[0];
    localStorage.setItem("caseType", this.caseTypeSelected.itemName);
    if (this.msalAuthenticationService.userInfo.idToken.preferred_username)
      this.email = this.msalAuthenticationService.userInfo.idToken.preferred_username;
    this.getProfileImage();
    // this.populateProfileInfo();
    this.$scope.$on("$stateChangeSuccess", () => {
      this.navbarRegistrationService.configure();
    });
    let keyPressBinding = this.keyPressed.bind(this);
    let $doc = angular.element(document);
    $doc.on("keydown", keyPressBinding);
    $scope.$on("$destroy", () => {
      $doc.off("keydown", keyPressBinding);
    });
  }

  // -- PUBLIC methods --

  public keyPressed(event: KeyboardEvent) {
    this.navbarRegistrationService.mapKeyboardShortcuts.forEach(
      (onActivated: (event: ng.IAngularEvent | KeyboardEvent) => void, key: string) => {
        if (event.keyCode === key.toUpperCase().charCodeAt(0) && event.altKey) {
          onActivated(event);
        }
      }
    );
  }

  // check whether user has DoD access rights and flight is enabled
  public hasDoDAccess(): boolean {
    return (
      this.authorizationService.hasRight("GetGeoRestrictedCases") &&
      this.flightingService.isFlightEnabled("CASES_ENABLE_GEO_RESTRICTION")
    );
  }

  public caseTypeClicked(clicked: SelectMenuItem) {
    if (!this.caseTypeSelected || this.caseTypeSelected.itemValue != clicked.itemValue) {
      this.caseTypeSelected = clicked;
      localStorage.setItem("caseType", this.caseTypeSelected.itemName);
    }
  }

  public customItemsClicked(menuItem: SelectMenuItem) {
    if (menuItem.itemValue) {
      menuItem.itemValue();
    }
  }

  public onHeaderActivated($event: ng.IAngularEvent | KeyboardEvent) {
    if (this.navbarRegistrationService.headerBrand.onActivated) {
      this.navbarRegistrationService.headerBrand.onActivated($event);
    }
  }

  getProfileImage(): void {
    this.graphApiService
      .getUserProfileImage()
      .then((data: any) => {
        if (data) {
          this.profileImgUrl = URL.createObjectURL(data);
        }
      })
      .catch((error: any) => {
        this.profileImgUrl = defaultImageUrl;
        this.loggingService.logError(
          error,
          null,
          this.sdLocalizationService.getLocalizedString("commercial", "_User_Profile_Image_Fail_To_Load_")
        );
      })
      .finally(() => {
        this.profileImgUrl = this.profileImgUrl;
      });
  }

  public populateProfileInfo() {
    this.profileImgUrl = this.applicationContextService.appContext.agentProfileImageUrl;
    if (this.applicationContextService.appContext.agentDisplayName) {
      this.agentName = this.applicationContextService.appContext.agentDisplayName;
    }
  }

  public skipToMain() {
    this.$scope.$emit("skipToMainContent");
  }

  // -- PRIVATE methods --

  private onStateChanged(
    event: AvailabilityEvent,
    oldState: ChannelsModel.ModalityAvailability,
    newState: ChannelsModel.ModalityAvailability
  ) {
    if (event === AvailabilityEvent.INFO_WEBFORM_PRESENCE_CHANGED) {
      if (!newState.reason || newState.reason == this.channelsConstants.availabilityReason.LogOut) {
        // change status to "NotAvailable"
        let notAvailableState: PresenceState = this.sdPresenceStatesCollection.findStateByReason(
          this.channelsConstants.availabilityReason.NotAvailable
        );
        this.updatePresence(notAvailableState);
      } else {
        this.setMenuState(this.sdPresenceStatesCollection.findState(newState));
      }
    }

    if (event === AvailabilityEvent.INFO_AVAILABILITY_UPDATED) {
      this.displayAvailabilityUpdatedAlert();
    }

    if (event === AvailabilityEvent.INFO_GET_AVAILABILITY_EMPTY) {
      this.displayAwayStatus();
    }
  }

  private hasRightsForPresence(): boolean {
    return this.authorizationService.hasRight(this.presenceRight, this.sdConfig.sdAuthorizationAppId);
  }

  private setMenuState(state: PresenceState) {
    if (state != undefined) {
      this.disableUpdatingProgressStatus();
      this.agentPresenceGlyph = state.glyph;
      this.agentPresenceString = state.displayName;
      this.previousPresence = this.currentPresence;
      this.currentPresence = state;
    } else {
      this.displayUnknownStatus();
    }
  }

  private updatePresence(state: PresenceState, changedByUser = false) {
    let modalityState = <AvailabilityModels.ModalityState>{
      modality: this.modalityConstants.WEBFORM,
      cantileverState: state.cantileverState
    };

    this.loggingService.logInfo({
      message: "Agent presence menu state changed",
      changedByUser: changedByUser,
      state: state,
      windowId: this.$window ? this.$window.name : undefined
    });

    this.displayUpdatingProgressStatus();
    this.sdkAvailabilityService
      .updatePresence(modalityState)
      .then(() => {
        this.agentPresenceGlyph = state.glyph;
        this.agentPresenceString = state.displayName;
        this.disableUpdatingProgressStatus();

        this.previousPresence = this.currentPresence;
        this.currentPresence = state;
      })
      .catch((result: any) => {
        // failed to update presence
        // revert UI back to previous presence state
        this.agentPresenceGlyph = this.currentPresence.glyph;
        this.agentPresenceString = this.currentPresence.displayName;
        this.disableUpdatingProgressStatus();
        let displayLocKey: string = this.getLocalizationKey(
          result.error,
          "_AGENT_DESKTOP_NAV_BAR_PRESENCE_AVAILABILITY_FAILED_ERROR_"
        );

        // notify user
        let userMessage: UserVisibleMessage = {
          displayText: this.sdLocalizationService.getLocalizedString(this.locAppId, displayLocKey),
          uId: displayLocKey
        };

        let logMessage: string =
          "Failed to update presence of modality '" +
          +modalityState.modality.getValue() +
          "' to state {" +
          modalityState.cantileverState.availabilityStatus +
          "," +
          modalityState.cantileverState.reason +
          "}";

        this.loggingService.logError(logMessage, result.error, userMessage);
      });
  }

  private displayAwayStatus() {
    if (!this.awayPresenceState) {
      this.awayPresenceState = new PresenceStateImplementation(
        this.awayLocId,
        this.awayGlyph,
        this.cantileverStateConstants.AWAY
      );
      this.awayPresenceState.resolveLoc(this.locAppId, this.sdLocalizationService);
    }

    this.setMenuState(this.awayPresenceState);
  }

  private displayUnknownStatus() {
    let unknownPresenceState: PresenceState = new PresenceStateImplementation(
      this.unknownLocId,
      this.unknownGlyph,
      this.cantileverStateConstants.UNKNOWN
    );
    unknownPresenceState.resolveLoc(this.locAppId, this.sdLocalizationService);
    this.setMenuState(unknownPresenceState);
  }

  private displayAvailabilityUpdatedAlert() {
    let userMessage: UserVisibleMessage = {
      displayText: this.sdLocalizationService.getLocalizedString(
        this.locAppId,
        "_AGENT_DESKTOP_NAV_BAR_PRESENCE_AVAILABILITY_CHANGE_MESSAGE_"
      ),
      uId: "_AGENT_DESKTOP_NAV_BAR_PRESENCE_AVAILABILITY_CHANGE_MESSAGE_"
    };
    this.loggingService.logInfo("Availability update event received.", userMessage);
  }

  private getMenuItems(states: PresenceState[]): MenuItem[] {
    if (states.length === 0) {
      return [];
    }

    let items: MenuItem[] = [];
    states.forEach((state) => {
      let subItems: MenuItem[] = this.getMenuItems(state.getSubStates());
      items.push({
        displayName: state.displayName,
        glyph: state.glyph,
        subItems: subItems.length > 0 ? subItems : undefined,
        onClick:
          state.displayName !== "Away"
            ? () => {
                this.updatePresence(state, true);
                this.$scope.$emit("OnClickStatusMenu");
              }
            : undefined,
        onKeydown:
          state.displayName === "Away"
            ? (event) => {
                if (!event.shiftKey && event.keyCode === 9) {
                  this.$scope.$emit("OnKeydownStatusMenu");
                } else {
                  this.$scope.$emit("OnClickMeetingMenu");
                }
              }
            : (event) => {
                if (state.displayName === "Meeting" && !event.shiftKey && event.keyCode === 9) {
                  this.$scope.$emit("OnClickMeetingMenu");
                }
              }
      });
    });
    return items;
  }

  private onSettingsClicked() {
    sessionStorage.setItem(parentStateName, this.$state.current.name.toLowerCase());
    this.$window.open("#/settings/", "_blank", "height=768,width=1024,location=no,resizable=yes,scrollbars=yes");
  }

  // Replace by configureMeDropdownWithAvailability, removed when flight is removed.
  private configureMeDropdown() {
    this.menuItems = [];
    if (this.msalAuthenticationService.userInfo.isAuthenticated) {
      // if (!this.flightingService.isFlightEnabled("Agent_Presence")) {
      //     this.menuItems.push({
      //         itemName: this.sdLocalizationService.getLocalizedString(this.locAppId, "_AGENT_DESKTOP_NAV_BAR_SETTINGS_BUTTON_LABEL_"),
      //         itemValue: () => {
      //             sessionStorage.setItem(ServiceDesk.Framework.Preferences.parentStateName, this.$state.current.name.toLowerCase());
      //             // TO DO: Create a new service to help us do this across our repo
      //             this.$window.open("#/settings/", "_blank", "height=768,width=1024,location=no,resizable=yes,scrollbars=yes");
      //         },
      //         automationId: "navbar-preferences",
      //     });
      // }
      this.menuItems.push({
        itemName: this.sdLocalizationService.getLocalizedString(
          this.locAppId,
          "_AGENT_DESKTOP_NAV_BAR_LOGOUT_BUTTON_LABEL_"
        ),
        itemValue: () => {
          this.logoutService.attemptLogout();
        },
        automationId: "navbar-sign-out"
      });
    }
  }

  private configureMeDropdownWithAvailability() {
    if (this.msalAuthenticationService.userInfo.isAuthenticated) {
      this.menuItemsWithStatus = [];

      // Presence menu
      if (this.hasRightsForPresence()) {
        this.displayUpdatingProgressStatus();
        this.sdkAvailabilityService
          .init([this.modalityConstants.WEBFORM], this.onStateChanged.bind(this))
          .then(() => {
            this.disableUpdatingProgressStatus();
          })
          .catch((result: any) => {
            this.loggingService.logError(
              "Availability service was not successful.",
              result.error,
              this.sdLocalizationService.getLocalizedString(
                this.locAppId,
                this.getLocalizationKey(result.error, "_AGENT_DESKTOP_NAV_BAR_PRESENCE_INIT_FAILED_ERROR_")
              )
            );
            this.displayUnknownStatus();
          });
        let allStates: PresenceState[] = [];
        this.sdPresenceStatesCollection.allStates.forEach((state) => {
          state.resolveLoc(this.locAppId, this.sdLocalizationService);
          if (state.showInMenu) {
            allStates.push(state);
          }
        });

        this.getMenuItems(allStates).forEach((item) => {
          this.menuItemsWithStatus.push(item);
        });
      }

      // Logout
      this.menuItemsWithStatus.push({
        displayName: this.sdLocalizationService.getLocalizedString(
          this.locAppId,
          "_AGENT_DESKTOP_NAV_BAR_LOGOUT_BUTTON_LABEL_"
        ),
        onClick: () => {
          if (this.availabilityServiceFlight && this.hasRightsForPresence()) {
            let modalityState = <AvailabilityModels.ModalityState>{
              modality: this.modalityConstants.WEBFORM,
              cantileverState: this.cantileverStateConstants.LOGOUT
            };

            this.sdkAvailabilityService
              .updatePresence(modalityState)
              .catch((result: ChannelsModel.PromiseResult) => {
                // failed to update presence
                let logMessage: string =
                  "Failed to update presence on logout'" +
                  +modalityState.modality.getValue() +
                  "' to state {" +
                  modalityState.cantileverState.availabilityStatus +
                  "," +
                  modalityState.cantileverState.reason +
                  "}";

                this.loggingService.logError(logMessage, result.error);
              })
              .finally(() => {
                this.logoutService.attemptLogout();
              });
          } else {
            this.logoutService.attemptLogout();
          }
        }
      });
    }
  }

  private displayUpdatingProgressStatus() {
    this.inStatusUpdate = true;
    this.agentPresenceString = this.sdLocalizationService.getLocalizedString(
      this.locAppId,
      "_AGENT_DESKTOP_NAV_BAR_PRESENCE_UPDATING_"
    );
  }

  private disableUpdatingProgressStatus() {
    this.inStatusUpdate = false;
  }

  /**
   * returns localization key based on error code in innererror object.
   * @param error error object returned from service
   * @param defaultLocalizationKey default localization key
   */
  private getLocalizationKey(error: any, defaultLocalizationKey: string): string {
    // check if status code returned is 403 as all agent configuration errors are returned with HttpStatusCodeForbidden
    if (error.context.statusCode && error.context.statusCode === this.channelsConstants.httpStatusCodeForbidden) {
      try {
        let errorCode: string = JSON.parse(error.context.message).error.innererror.code;
        switch (errorCode) {
          case "3010":
          case "3011":
          case "3016":
          case "3017":
          case "3018":
          case "3019":
          case "3037":
          case "3050":
          case "3051":
            return `_AGENT_DESKTOP_NAV_BAR_PRESENCE_AVAILABILITY_FAILED_ERROR_${errorCode}_`;
          default:
            return `_AGENT_DESKTOP_NAV_BAR_PRESENCE_AVAILABILITY_FAILED_ERROR_DEFAULT_`;
        }
      } catch (error) {
        this.loggingService.logError("Unable to parse error message returned from availablility service", error);
      }
    }
    return defaultLocalizationKey;
  }
}
